// @flow

import React from "react";
import { localize } from "redux-i18n";
import { SectionComponent, GSGrid, ChartJS, View } from "@components";
import type {
  ReportData,
  DepartmentProfileState,
} from "@modules/reporting/reducer/focus-lists/department/types";

type OwnProps = $Exact<{
  selectedDepartment: ReportData,
  departmentProfile: DepartmentProfileState,
  targetAbsenceRate: number,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
}>;

export const FocusListDepartmentProfile = ({
  departmentProfile,
  selectedDepartment,
  t,
  targetAbsenceRate,
}: Props) => (
  <View gap={6} margin="1.5rem 0 0 0">
    {selectedDepartment && (
      <GSGrid
        height={82}
        columns={[
          {
            name: t("HEADCOUNT"),
            key: "headCount",
          },
          {
            name: t("ABSENCE_RATE"),
            key: "absenceRate",
          },
          {
            name: t("DAYS_LOST_PER_EMPLOYEE"),
            key: "daysLostPerEmployee",
          },
          {
            name: t("TOTAL_DAYS_LOST"),
            key: "totalDaysLost",
          },
          {
            name: t("TOTAL_ABSENCES_SPELLS"),
            key: "totalAbsences",
          },
          {
            name: t("TOTAL_ALERTS"),
            key: "totalAlerts",
          },
        ]}
        rows={[selectedDepartment]}
      />
    )}

    {departmentProfile && (
      <>
        <View>
          <ChartJS
            type="line"
            className="mt-4"
            title={t("ABSENCE_RATE_LAST_12_MONTHS")}
            data={{
              labels: departmentProfile.absenceRatePerMonth.map(item => item.description),
              datasets: [
                {
                  label: t("ACTUAL_ABSENCE_RATE"),
                  data: departmentProfile.absenceRatePerMonth.map(item => item.value),
                },
                {
                  label: t("TARGET_ABSENCE_RATE"),
                  data: departmentProfile.absenceRatePerMonth.map(() => targetAbsenceRate),
                },
              ],
            }}
          />
        </View>
        <View>
          <ChartJS
            type="bar"
            className="mt-4"
            title={t("TOP_5_ABSENCE_REASONS_BY_WORKING_DAYS_LOST")}
            data={{
              labels: departmentProfile.top5AbsenceReasonsByWorkingDaysLost.map(
                item => item.description,
              ),
              datasets: [
                {
                  label: "",
                  data: departmentProfile.top5AbsenceReasonsByWorkingDaysLost.map(
                    item => item.value,
                  ),
                },
              ],
            }}
            options={{ wrapXAxisLabels: true }}
          />
        </View>
        <ChartJS
          type="bar"
          className="mt-4"
          title={t("TOP_10_EMPLOYEES_BY_WORKING_DAYS_LOST")}
          data={{
            labels: departmentProfile.top10EmployeesByWorkingDaysLost.map(item => item.description),
            datasets: [
              {
                label: "",
                data: departmentProfile.top10EmployeesByWorkingDaysLost.map(item => item.value),
              },
            ],
          }}
          options={{ wrapXAxisLabels: true }}
        />
        <View>
          <ChartJS
            type="bar"
            className="mt-4"
            title={t("TRENDS_ABSENCE_REASON_DLPE_BY_GENDER_TITLE")}
            data={{
              labels: departmentProfile.daysLostPerEmployeeByGender.map(item =>
                t(item.description),
              ),
              datasets: [
                {
                  label: "",
                  data: departmentProfile.daysLostPerEmployeeByGender.map(item => item.value),
                },
              ],
            }}
            options={{ wrapXAxisLabels: true }}
          />
        </View>
        <View>
          <ChartJS
            type="bar"
            className="mt-4"
            title={t("TRENDS_ABSENCE_REASON_DLPE_BY_AGE_GROUP_TITLE")}
            data={{
              labels: departmentProfile.daysLostPerEmployeeByAgeGroup.map(item =>
                t(item.description),
              ),
              datasets: [
                {
                  label: "",
                  data: departmentProfile.daysLostPerEmployeeByAgeGroup.map(item => item.value),
                },
              ],
            }}
            options={{ wrapXAxisLabels: true }}
          />
        </View>
      </>
    )}
  </View>
);

export default localize()(FocusListDepartmentProfile);
